import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DownloadIcon from "@mui/icons-material/Download";
import type { PublicationInfo } from "./useTaskDetailSubscription";
import { getFailedItemsWord } from "./GetFailedItemsWord";
import { groupItemsByScheduleDay } from "./GroupItemsByScheduleDay";
import { IfPermission } from "../../auth/IfPermission";
import { RescheduleDeferredDialog } from "./RescheduleDeferredDialog";
import { CancelDeferredDialog } from "./CancelDeferredDialog";

const PUBLICATION_TYPE_LABELS: Record<PublicationInfo["type"], string> = {
  article: "Статья",
  homepage: "Homepage",
  sitewide: "Sitewide",
};

const PUBLICATION_TYPE_COLORS: Record<PublicationInfo["type"], "primary" | "secondary" | "info"> = {
  article: "primary",
  homepage: "secondary",
  sitewide: "info",
};

const TABULAR_NUMS = { fontVariantNumeric: "tabular-nums" } as const;

// Исход действия над публикацией: `kind` — фаза (idle/loading/error), текст
// отказа приходит вместе с ошибкой.
interface ActionOutcome {
  kind: string;
  // Отсутствует, пока действие не провалилось: текст показывают только вместе
  // с состоянием ошибки.
  message?: string;
}

export function PublicationSection(props: {
  publication: PublicationInfo;
  onStop: (pubId: string) => void;
  stopState: { kind: string };
  onResume: (pubId: string) => void;
  resumeState: { kind: string };
  onReschedule: (pubId: string, newScheduledAt: string) => void;
  rescheduleState: ActionOutcome;
  onCancelDeferred: (pubId: string) => void;
  cancelDeferredState: ActionOutcome;
  onRetryFailed: (pubId: string) => void;
  retryFailedState: ActionOutcome;
  onEnsureIndexing: () => void;
  ensureIndexingState: { kind: string };
  isDeactivated: boolean;
  onExport: () => void;
  exportState: { kind: string };
}) {
  const { publication } = props;
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [retryFailedDialogOpen, setRetryFailedDialogOpen] = useState(false);

  const now = new Date();
  const allItemsCompleted = publication.items.every((item) => item.status !== "pending");
  const hasFailedItems = publication.items.some((item) => item.status === "failed");
  const publishedItems = publication.items.filter((item) => item.status === "published").length;
  const failedItems = publication.items.filter((item) => item.status === "failed").length;
  const totalItems = publication.items.length;
  const successShare = (publishedItems / totalItems) * 100;
  const successRate = totalItems > 0 ? Math.round(successShare) : 0;
  const isFullSuccess = publishedItems === totalItems;
  const isCancelled = publication.status === "cancelled";

  const outcomeColor = isFullSuccess ? "success.main" : "error.main";
  const completionColor = allItemsCompleted ? outcomeColor : "text.secondary";
  const failedItemsWord = getFailedItemsWord(failedItems);
  const scheduleRows = groupItemsByScheduleDay(publication.items, now);
  const hasSchedule = scheduleRows.length > 0;

  const hasFuturePendingItem = publication.items.some((item) => {
    const isPendingWithSchedule = item.status === "pending" && item.scheduledAt !== null;
    return isPendingWithSchedule && new Date(String(item.scheduledAt)).getTime() > now.getTime();
  });
  const canReschedule = publication.status === "scheduled";
  const hasDeferredActiveItem = publication.status === "active" && hasFuturePendingItem;
  const canCancelDeferred = publication.status === "scheduled" || hasDeferredActiveItem;

  function handleRescheduleConfirm(newScheduledAt: string) {
    props.onReschedule(publication.id, newScheduledAt);
    setRescheduleDialogOpen(false);
  }

  function handleCancelConfirm() {
    props.onCancelDeferred(publication.id);
    setCancelDialogOpen(false);
  }

  function handleRetryFailedConfirm() {
    props.onRetryFailed(publication.id);
    setRetryFailedDialogOpen(false);
  }

  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
        <Typography variant="h6">Публикация</Typography>
        <Chip
          label={PUBLICATION_TYPE_LABELS[publication.type]}
          size="small"
          color={PUBLICATION_TYPE_COLORS[publication.type]}
        />
        {publication.autoStarted && (
          <Chip label="Автозапуск" size="small" color="info" variant="outlined" />
        )}
        {totalItems > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: completionColor,
              fontWeight: 500,
              ...TABULAR_NUMS,
            }}
          >
            {publishedItems}/{totalItems} · {successRate}%
          </Typography>
        )}
        {!allItemsCompleted &&
          (publication.status === "active" || publication.status === "scheduled") && (
            <IfPermission permission="publications.manage">
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => props.onStop(publication.id)}
                disabled={props.stopState.kind === "loading"}
              >
                {props.stopState.kind === "loading" ? "Остановка…" : "Остановить"}
              </Button>
            </IfPermission>
          )}
        {!allItemsCompleted && publication.status === "paused" && (
          <IfPermission permission="publications.manage">
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => props.onResume(publication.id)}
              disabled={props.resumeState.kind === "loading"}
            >
              {props.resumeState.kind === "loading" ? "Возобновление…" : "Возобновить"}
            </Button>
          </IfPermission>
        )}
        {canReschedule && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRescheduleDialogOpen(true)}
            disabled={props.rescheduleState.kind === "loading"}
          >
            {props.rescheduleState.kind === "loading" ? "Перенос…" : "Перенести публикацию"}
          </Button>
        )}
        {canCancelDeferred && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => setCancelDialogOpen(true)}
            disabled={props.cancelDeferredState.kind === "loading"}
          >
            {props.cancelDeferredState.kind === "loading" ? "Отмена…" : "Отменить отложенный остаток"}
          </Button>
        )}
        {publication.status !== "cancelled" && hasFailedItems && (
          <>
            <Typography variant="body2" color="error">
              {failedItems} {failedItems === 1 ? "публикация не удалась" : "публикаций не удались"}
            </Typography>
            <IfPermission permission="publications.manage">
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setRetryFailedDialogOpen(true)}
                disabled={props.retryFailedState.kind === "loading"}
              >
                {props.retryFailedState.kind === "loading" ? "Повтор…" : "Повторить неудачные"}
              </Button>
            </IfPermission>
          </>
        )}
        {publication.type === "article" && !props.isDeactivated && (
          <IfPermission permission="publications.indexing">
            <Button
              size="small"
              variant="outlined"
              onClick={props.onEnsureIndexing}
              disabled={props.ensureIndexingState.kind === "loading"}
            >
              {props.ensureIndexingState.kind === "loading" ? "Постановка…" : "Переиндексировать публикации"}
            </Button>
          </IfPermission>
        )}
        <IfPermission permission="publications.export">
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={props.onExport}
            disabled={props.exportState.kind === "loading"}
          >
            {props.exportState.kind === "loading" ? "Экспорт…" : "Экспорт CSV"}
          </Button>
        </IfPermission>
      </Box>

      {props.rescheduleState.kind === "error" && (
        <Alert severity="error" sx={{ mt: 1 }}>{props.rescheduleState.message}</Alert>
      )}
      {props.cancelDeferredState.kind === "error" && (
        <Alert severity="error" sx={{ mt: 1 }}>{props.cancelDeferredState.message}</Alert>
      )}
      {props.retryFailedState.kind === "error" && (
        <Alert severity="error" sx={{ mt: 1 }}>{props.retryFailedState.message}</Alert>
      )}

      {hasSchedule && (
        <Box mt={2} sx={{ overflowX: "auto" }}>
          <Typography variant="subtitle2" gutterBottom>
            Расписание публикации
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Время запуска</TableCell>
                <TableCell align="right">Запланировано</TableCell>
                <TableCell align="right">Опубликовано</TableCell>
                {isCancelled && <TableCell>Статус</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduleRows.map((row) => {
                const isCancelledFutureRow = isCancelled && !row.isPast;
                const highlightUpcoming = row.isUpcoming && !isCancelled;
                return (
                  <TableRow
                    key={`${row.dateLabel}-${row.timeLabel}`}
                    sx={{
                      opacity: row.isPast ? 0.5 : 1,
                      bgcolor: highlightUpcoming ? "action.selected" : undefined,
                    }}
                  >
                    <TableCell sx={{ fontWeight: highlightUpcoming ? 600 : undefined, ...TABULAR_NUMS }}>
                      {row.dateLabel}
                    </TableCell>
                    <TableCell sx={TABULAR_NUMS}>{row.timeLabel}</TableCell>
                    <TableCell className="tabular-nums" align="right" sx={TABULAR_NUMS}>{row.scheduledCount}</TableCell>
                    <TableCell className="tabular-nums" align="right" sx={TABULAR_NUMS}>{row.publishedCount}</TableCell>
                    {isCancelled && (
                      <TableCell>
                        {isCancelledFutureRow && (
                          <Chip label="Отменено" size="small" color="default" variant="outlined" />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <RescheduleDeferredDialog
        open={rescheduleDialogOpen}
        onClose={() => setRescheduleDialogOpen(false)}
        onConfirm={handleRescheduleConfirm}
      />

      <CancelDeferredDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
      />

      <Dialog open={retryFailedDialogOpen} onClose={() => setRetryFailedDialogOpen(false)}>
        <DialogTitle>Повторить неудачные публикации</DialogTitle>
        <DialogContent>
          <Typography>
            Будет повторена публикация для {failedItems} {failedItemsWord}. Продолжить?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryFailedDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleRetryFailedConfirm} color="error" variant="contained">
            Повторить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
